import {
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Input,
    Autocomplete,
    AutocompleteItem,
    Textarea,
} from "@heroui/react";
import { useEffect } from "react";
import { usePostData, useFetchData } from "@/hooks";
import { showToast } from "@/utils";
import { Controller, useForm } from "react-hook-form";
import { ThemedText } from "@/components/ui";

const KandangFormModal = ({
    isOpen,
    onClose,
    mode = "create",
    initialData = null,
    onSuccess,
}) => {
    const isEdit = mode === "edit";

    const defaultValues = {
        name: "",
        pic_id: "",
        description: "",
    };

    const {
        control,
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues,
    });

    // Fetch Staff for PIC
    const { data: staffList, isLoading: isStaffLoading } =
        useFetchData("select/staff");

    // Mutation
    const endpoint = isEdit ? `coops/${initialData?.id}/update` : "coops/store";
    const mutation = usePostData(endpoint);

    useEffect(() => {
        if (isOpen) {
            reset(initialData || defaultValues);
        }
    }, [isOpen, initialData, reset]);

    const handleClose = () => {
        reset(defaultValues);
        onClose();
    };

    const onSubmit = (data) => {
        mutation.mutate(data, {
            method: isEdit ? "put" : "post",
            onSuccess: () => {
                showToast(
                    "success",
                    isEdit ? "Update Successful" : "All Set!",
                    isEdit
                        ? "Kandang updated successfully!"
                        : "Kandang created successfully!",
                );
                onSuccess?.();
                handleClose();
            },
            onError: (err) => {
                console.error("Failed to submit coop:", err);
                let message =
                    "Terjadi kesalahan pada sistem. Mohon coba beberapa saat lagi.";

                if (err.response?.status === 422) {
                    message = "Mohon periksa kembali input Anda.";
                }

                showToast("error", "Gagal Menyimpan", message);
            },
        });
    };

    return (
        <Modal
            isOpen={isOpen}
            onOpenChange={(open) => !open && handleClose()}
            placement="center"
            classNames={{
                header: "border-b border-default-100",
                footer: "border-t border-default-100",
            }}
            isDismissable={!mutation.isLoading}
            isKeyboardDismissDisabled={mutation.isLoading}
            hideCloseButton={mutation.isLoading}
        >
            <ModalContent>
                {() => (
                    <form onSubmit={handleSubmit(onSubmit)} noValidate>
                        <ModalHeader className="flex flex-col gap-1">
                            <ThemedText size="md">
                                {isEdit
                                    ? "Edit Kandang"
                                    : "Tambah Kandang Baru"}
                            </ThemedText>
                        </ModalHeader>
                        <ModalBody className="py-4">
                            <Input
                                autoFocus
                                label="Nama Kandang"
                                placeholder="Masukkan nama kandang"
                                variant="bordered"
                                isRequired
                                isInvalid={!!errors.name}
                                errorMessage={errors.name?.message}
                                {...register("name", {
                                    required: "Nama kandang wajib diisi",
                                })}
                            />

                            <Controller
                                name="pic_id"
                                control={control}
                                render={({ field }) => (
                                    <Autocomplete
                                        label="PIC Kandang"
                                        placeholder="Pilih PIC"
                                        variant="bordered"
                                        defaultItems={staffList || []}
                                        isLoading={isStaffLoading}
                                        selectedKey={
                                            field.value
                                                ? String(field.value)
                                                : null
                                        }
                                        onSelectionChange={field.onChange}
                                    >
                                        {(item) => (
                                            <AutocompleteItem key={item.value}>
                                                {item.title}
                                            </AutocompleteItem>
                                        )}
                                    </Autocomplete>
                                )}
                            />

                            <Textarea
                                label="Catatan Tambahan"
                                placeholder="Isikan catatan tambahan jika ada"
                                variant="bordered"
                                {...register("description")}
                            />
                        </ModalBody>
                        <ModalFooter>
                            <Button
                                variant="flat"
                                onPress={handleClose}
                                size="sm"
                                isDisabled={mutation.isLoading}
                            >
                                Batal
                            </Button>
                            <Button
                                color="primary"
                                type="submit"
                                isLoading={mutation.isLoading}
                                size="sm"
                            >
                                {isEdit ? "Update Kandang" : "Tambah Kandang"}
                            </Button>
                        </ModalFooter>
                    </form>
                )}
            </ModalContent>
        </Modal>
    );
};

export default KandangFormModal;
